// post-login redirect target — capture where an unauthenticated user wanted to go,
// then send them there once they sign in. shared by the route guard (in-SPA cold case)
// and the axios 401 interceptor (mid-session expiry, which hard-reloads to /login).

export const POST_LOGIN_REDIRECT_KEY = 'fp3_post_login_redirect'

// a sentinel origin used to resolve relative paths; anything that escapes it is unsafe
const SENTINEL_ORIGIN = 'http://localhost'

// public/auth routes we must never bounce back to — doing so would defeat the redirect
// or trap the user in a login loop
const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/check-email',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
]

// a value is a safe redirect target only when it is a same-origin, in-app path that is
// not itself an auth page. this guards against open redirects via attacker-supplied
// absolute, protocol-relative, or backslash-escaped URLs.
export const isSafeRedirectTarget = (path: unknown): path is string => {
  if (typeof path !== 'string' || path.length === 0) return false
  // a leading slash is required so the value cannot be an absolute URL
  if (!path.startsWith('/')) return false
  try {
    // resolving against the sentinel origin normalizes tricks like "//host" and "/\host";
    // anything that lands on a different origin is rejected
    const resolved = new URL(path, SENTINEL_ORIGIN)
    if (resolved.origin !== SENTINEL_ORIGIN) return false
    return !PUBLIC_PATHS.includes(resolved.pathname)
  } catch {
    return false
  }
}

// persist where to send the user once they authenticate; survives a hard reload.
// best-effort — sessionStorage may be unavailable (e.g. private mode).
export const saveRedirectTarget = (path: string): void => {
  if (!isSafeRedirectTarget(path)) return
  try {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path)
  } catch {
    // ignore — the redirect is a convenience, not a guarantee
  }
}

// read and remove the persisted target, returning it only when it is still a safe path
export const consumeRedirectTarget = (): string | null => {
  let stored: string | null = null
  try {
    stored = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
  } catch {
    return null
  }
  return isSafeRedirectTarget(stored) ? stored : null
}

// build a root-relative path string from a location-like object, preserving query and hash
const toPath = (location: unknown): string | null => {
  if (!location || typeof location !== 'object') return null
  const { pathname, search, hash } = location as {
    pathname?: unknown
    search?: unknown
    hash?: unknown
  }
  if (typeof pathname !== 'string') return null
  return `${pathname}${typeof search === 'string' ? search : ''}${typeof hash === 'string' ? hash : ''}`
}

// decide where a freshly-authenticated user should land. always clears any persisted
// target, then prefers the location captured by the route guard, falling back to the
// persisted target (set before a hard 401 reload), and finally the default landing page.
export const resolvePostLoginDestination = (capturedFrom: unknown): string => {
  const stored = consumeRedirectTarget()
  const fromGuard = toPath(capturedFrom)
  if (fromGuard && isSafeRedirectTarget(fromGuard)) return fromGuard
  return stored ?? '/'
}
